import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { apiService } from "../../../services/api";
import { Produto, CreateProdutoData, UnidadeMedida } from '../../../types/produtos';
import { Offcanvas, Modal } from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { modalUtils } from '../../../utils/modalUtils';
import { AdicionarEstoqueModal } from './AdicionarEstoqueModal';
import { HistoricoPrecoModal } from './HistoricoPrecoModal';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import ProdutosRelacionadosModal from '../../../components/ProdutosRelacionadosModal';
import { searchProdutosRelacionados } from '../../../utils/produtoSearch';
import { useUnidadesMedida } from '../../../hooks/useUnidadesMedida';
import Select from 'react-select';

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: Produto) => React.ReactNode;
  sorter?: (a: Produto, b: Produto) => number;
  key?: string;
}

export const Produtos = () => {
  const dispatch = useDispatch();
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [filteredProdutos, setFilteredProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingProdutoId, setDeletingProdutoId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Estado para controlar o produto em edição
  const [editingProduto, setEditingProduto] = useState<Produto | null>(null);
  const [newProduto, setNewProduto] = useState<CreateProdutoData>({
    nome: '',
    descricao: '',
    marca: '',
    estoque: 0,
    unidade_medida: 'unidade' as UnidadeMedida
  });

  const { unidadesMedida } = useUnidadesMedida();
  const unidadeOptions = unidadesMedida.map(u => ({
    value: u.codigo,
    label: `${u.nome} (${u.codigo})`
  }));

  // Estado para o modal de histórico de preços
  const [produtoHistorico, setProdutoHistorico] = useState<Produto | null>(null);

  // Estado para o modal de produtos relacionados
  const [showRelacionadosModal, setShowRelacionadosModal] = useState(false);
  const [produtosRelacionados, setProdutosRelacionados] = useState<Produto[]>([]);

  // Estado para controlar o modal de adicionar estoque
  const [produtoParaEstoque, setProdutoParaEstoque] = useState<{
    id: number;
    nome: string;
    estoque: number;
  } | null>(null);


  // Função para buscar produtos
  const fetchProdutos = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProdutos();

      // Adiciona a propriedade key para cada produto (necessária para o Ant Design Table)
      const produtosComKey = response.data.map((produto: Produto) => ({
        ...produto,
        key: produto.id.toString()
      }));

      setProdutos(produtosComKey);
      setFilteredProdutos(produtosComKey);
      setError(null);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setError('Erro ao carregar os produtos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Carregar produtos ao montar o componente
  useEffect(() => {
    fetchProdutos();
  }, []);

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredProdutos(produtos);
  }, [produtos]);

  // Função para filtrar produtos quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredProdutos(produtos);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = produtos.filter(produto => {
        // Campos a serem pesquisados
        const searchableFields = [
          produto.nome,
          produto.descricao,
          produto.marca,
          produto.ativo ? 'inativo' : 'ativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredProdutos(filtered);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, produtos]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingProduto(null);
  };

  // Função para limpar o estado do novo produto
  const handleCancelNew = () => {
    setNewProduto({
      nome: '',
      descricao: '',
      marca: '',
      estoque: 0,
      unidade_medida: 'unidade' as UnidadeMedida
    });
  };

  // Função para preparar a edição
  const handleEdit = (produto: Produto) => {
    setEditingProduto(produto);
  };

  // Abre modal de produtos relacionados ao tirar foco do nome
  const handleNomeProdutoBlur = () => {
    if (!newProduto.nome.trim()) return;
    const relacionados = searchProdutosRelacionados(produtos, newProduto.nome);
    if (relacionados.length > 0) {
      setProdutosRelacionados(relacionados);
      setShowRelacionadosModal(true);
    }
  };

  const handleUsarProdutoExistente = () => {
    setShowRelacionadosModal(false);
    handleCancelNew();
    const offcanvasEl = document.getElementById('offcanvas_produto');
    if (offcanvasEl) {
      const instance = Offcanvas.getInstance(offcanvasEl);
      if (instance) instance.hide();
    }
    setTimeout(() => {
      document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('offcanvas-open');
    }, 300);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (produto: Produto) => {
    setDeletingProdutoId(produto.id);
  };

  // Função para abrir modal de adicionar estoque
  const handleAbrirModalEstoque = (produto: Produto) => {
    setProdutoParaEstoque({
      id: produto.id,
      nome: produto.nome,
      estoque: produto.estoque
    });
    modalUtils.abrirModal('modal_adicionar_estoque');
  };

  // Função para indicar estoque baixo
  const isEstoqueBaixo = (estoque: number) => estoque <= 10;
  const isEstoqueZero = (estoque: number) => estoque === 0;

  // Manipulador de seleções da tabela
  const handleSelectionChange = (selectedRowKeys: any[]) => {
    // Aqui você pode fazer qualquer coisa com as chaves selecionadas
    console.log('Produtos selecionados:', selectedRowKeys);
  };

  const columns: TableColumn[] = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      render: (nome) => (
        <div
          style={{
            maxWidth: "300px", // Aumentado já que removemos outras colunas
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          title={nome} // Mostra o texto completo ao passar o mouse
        >
          {nome}
        </div>
      ),
    },
    {
      title: "Unidade",
      dataIndex: "unidade_medida",
      key: "unidade_medida",
      render: (unidade_medida) => (
        <div
          style={{
            maxWidth: "300px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          title={unidade_medida}
        >
          {unidade_medida || '—'}
        </div>
      ),
      sorter: (a, b) => (a.unidade_medida || '').localeCompare(b.unidade_medida || ''),
    },
    {
      title: "Estoque Atual",
      dataIndex: "estoque",
      key: "estoque",
      render: (estoque, record) => (
        <div className="d-flex align-items-center">
          {isEstoqueZero(estoque) && (
            <i className="ti ti-alert-circle text-danger me-2" title="Estoque zerado"></i>
          )}
          {!isEstoqueZero(estoque) && isEstoqueBaixo(estoque) && (
            <i className="ti ti-alert-triangle text-warning me-2" title="Estoque baixo"></i>
          )}
          {!isEstoqueBaixo(estoque) && (
            <i className="ti ti-check-circle text-success me-2" title="Estoque adequado"></i>
          )}
          <span className={isEstoqueZero(estoque) ? 'text-danger fw-bold' : isEstoqueBaixo(estoque) ? 'text-warning fw-bold' : ''}>
            {new Intl.NumberFormat('pt-BR').format(estoque)}
          </span>
        </div>
      ),
      sorter: (a, b) => a.estoque - b.estoque,
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (ativo) => (
        <div>
          {ativo ? (
            <span className="badge badge-pill badge-status bg-danger">Inativo</span>
          ) : (
            <span className="badge badge-pill badge-status bg-success">Ativo</span>
          )}
        </div>
      ),
      sorter: (a, b) => {
        const aValue = a.ativo ? 1 : 0;
        const bValue = b.ativo ? 1 : 0;
        return aValue - bValue;
      },
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            title="Histórico de Preços em Oportunidades"
            onClick={() => setProdutoHistorico(record)}
          >
            <i className="ti ti-chart-bar" />
          </button>
          <ProtectedComponent module="PRODUTOS" action="GERENCIAR_ESTOQUE">
            <Link
              to="#"
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleAbrirModalEstoque(record)}
              title="Gerenciar Estoque"
            >
              <i className="ti-package"></i>
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="PRODUTOS" action="EDITAR">
            <Link
              to="#"
              className="btn btn-sm btn-outline-info"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleEdit(record)}
              title="Editar"
            >
              <i className="ti ti-edit"></i>
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="PRODUTOS" action="EXCLUIR">
            <Link
              to="#"
              className="btn btn-sm btn-outline-danger"
              data-bs-toggle="modal"
              data-bs-target="#delete_produto"
              onClick={() => handlePrepareDelete(record)}
              title="Excluir"
            >
              <i className="ti ti-trash"></i>
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  // Função para limpar modal
  const limparModal = (idModal: string) => {
    // Usar nosso utilitário de modal
    modalUtils.fecharModal(idModal, () => {
      // Callback específico após fechar o modal
      if (idModal === 'success_delete_modal') {
        setDeletingProdutoId(null);
      }
    });
  };

  // Função para criar novo produto
  const handleCreateProduto = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      // Validações
      if (!newProduto.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }


      if (newProduto.estoque < 0) {
        alert('O estoque não pode ser negativo');
        return;
      }

      await apiService.createProduto(newProduto);
      await fetchProdutos();

      handleCancelNew();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_produto');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer backdrop de offcanvas
      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('offcanvas-open');

        // Mostra modal de sucesso usando o utilitário
        modalUtils.abrirModal('success_modal');
      }, 300);

    } catch (error) {
      console.error('Erro ao criar produto:', error);
      alert('Erro ao criar o produto. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar produto
  const handleUpdateProduto = async () => {
    if (!editingProduto?.id) return;

    try {
      setIsSubmitting(true);

      // Validações
      if (!editingProduto.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }


      if (editingProduto.estoque < 0) {
        alert('O estoque não pode ser negativo');
        return;
      }

      await apiService.updateProduto(editingProduto.id, {
        nome: editingProduto.nome,
        descricao: editingProduto.descricao,
        marca: editingProduto.marca,
        estoque: editingProduto.estoque,
        unidade_medida: editingProduto.unidade_medida
      });

      await fetchProdutos();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer backdrop de offcanvas
      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('offcanvas-open');

        // Mostra modal de sucesso usando o utilitário
        modalUtils.abrirModal('success_edit_modal');
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      alert('Erro ao atualizar o produto. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar produto
  const handleDeleteProduto = async () => {
    if (!deletingProdutoId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteProduto(deletingProdutoId);
      await fetchProdutos();

      // Fecha o modal de confirmação usando o utilitário
      modalUtils.fecharModal('delete_produto');

      // Mostra modal de sucesso após um breve delay
      setTimeout(() => {
        modalUtils.abrirModal('success_delete_modal');
      }, 400);

    } catch (error) {
      console.error('Erro ao excluir produto:', error);
      alert('Erro ao excluir o produto. Por favor, tente novamente.');

      // Garantir limpeza mesmo em caso de erro
      modalUtils.limparEfeitosModal();
    } finally {
      setIsDeleting(false);
    }
  };


  // Formatação de número
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Produtos<span className="count-title">{filteredProdutos.length} produtos</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Produto"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Maior Estoque
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Menor Estoque
                          </Link>
                        </div>
                      </div>


                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="PRODUTOS" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_produto"
                        >
                          <i className="ti ti-plus me-2" />
                          Adicionar Produto
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  {/* Tabela de Produtos */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredProdutos.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum produto encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredProdutos.map(produto => ({
                          ...produto,
                          key: produto.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Novo Produto */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_produto">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Produto</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateProduto}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={newProduto.nome}
                onChange={(e) => setNewProduto(prev => ({
                  ...prev,
                  nome: e.target.value
                }))}
                onBlur={handleNomeProdutoBlur}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Unidade de Medida</label>
              <Select
                className="select2"
                options={unidadeOptions}
                value={unidadeOptions.find(opt => opt.value === newProduto.unidade_medida) || null}
                onChange={(option) => setNewProduto(prev => ({
                  ...prev,
                  unidade_medida: (option?.value || 'unidade') as UnidadeMedida
                }))}
                placeholder="Selecione..."
                classNamePrefix="react-select"
                isSearchable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Estoque Inicial
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                value={newProduto.estoque}
                onChange={(e) => setNewProduto(prev => ({
                  ...prev,
                  estoque: parseFloat(e.target.value) || 0
                }))}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Offcanvas Editar Produto */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Produto</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateProduto();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={editingProduto?.nome || ''}
                onChange={(e) => setEditingProduto(prev => prev ? {
                  ...prev,
                  nome: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Unidade de Medida</label>
              <Select
                className="select2"
                options={unidadeOptions}
                value={unidadeOptions.find(opt => opt.value === editingProduto?.unidade_medida) || null}
                onChange={(option) => setEditingProduto(prev => prev ? {
                  ...prev,
                  unidade_medida: (option?.value || 'unidade') as UnidadeMedida
                } : null)}
                placeholder="Selecione..."
                classNamePrefix="react-select"
                isSearchable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Estoque
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-control"
                value={editingProduto?.estoque || 0}
                onChange={(e) => setEditingProduto(prev => prev ? {
                  ...prev,
                  estoque: parseFloat(e.target.value) || 0
                } : null)}
              />
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_produto" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Produto?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o produto selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingProdutoId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteProduto}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Criação */}
      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Produto Criado com Sucesso!</h3>
                <p>O produto foi criado e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Produto Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Produto Excluído com Sucesso!</h3>
                <p>O produto foi removido permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Produtos Relacionados */}
      {showRelacionadosModal && (
        <ProdutosRelacionadosModal
          searchTerm={newProduto.nome}
          produtos={produtosRelacionados}
          primaryAction={{
            label: 'Utilizar produto existente',
            className: 'btn-outline-primary',
            onClick: handleUsarProdutoExistente,
          }}
          onContinue={() => setShowRelacionadosModal(false)}
          onClose={() => setShowRelacionadosModal(false)}
        />
      )}

      {/* Modal Adicionar Estoque */}
      {produtoParaEstoque && (
        <AdicionarEstoqueModal
          produtoId={produtoParaEstoque.id}
          produtoNome={produtoParaEstoque.nome}
          estoqueAtual={produtoParaEstoque.estoque}
          onEstoqueAdicionado={fetchProdutos}
        />
      )}

      {/* Modal Histórico de Preços */}
      {produtoHistorico && (
        <HistoricoPrecoModal
          produto={produtoHistorico}
          onHide={() => setProdutoHistorico(null)}
        />
      )}
    </>
  );
};